/**
 * Shared types for the notification engine's configuration masters.
 *
 * The engine's routers page with `skip`/`limit` rather than the `page`/`page_size`
 * the older masters use, so its list params get their own type here instead of
 * being borrowed from a service that means something different by them.
 */

export interface ApiEnvelope<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export interface PaginatedEnvelope<T> {
  success: boolean;
  data: T[];
  pagination?: { total?: number; totalItems?: number; page?: number; pageSize?: number };
}

/** Every engine master lists on the same params. */
export interface EngineListParams {
  skip?: number;
  limit?: number;
  includeDeleted?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Free-text search across the master's searchable fields. */
  search?: string;
  /** Exact category (notification types). */
  category?: string;
  /** A delivery channel the master uses. */
  channel?: string;
  /** Restrict to one notification type (notifications). */
  notificationTypeId?: string;
  /** Restrict to one recipient kind (recipients). */
  recipientType?: string;
  /** Split audiences (false) from do-not-contact lists (true). */
  isDoNotContact?: boolean;
  /** Restrict to one project (appId); omit for all accessible projects. */
  appId?: string;
  /** Also include core/global rows (appId=null) — the "Show core" toggle. */
  include_global?: boolean;
}

/** Which column a list is ordered by, and which way. */
export interface SortState {
  field: string | null;
  order: 1 | -1 | null;
}

export const NO_SORT: SortState = { field: null, order: null };

/** Where every master list starts: the most recently created row at the top. */
export const NEWEST_FIRST: SortState = { field: 'createdAt', order: -1 };

/** Where a scheduler starts: whatever fires next at the top. */
export const SOONEST_NEXT_RUN: SortState = { field: 'nextRunAt', order: 1 };

/**
 * A sort state as query params, or nothing at all.
 *
 * Nothing, rather than an explicit default: with no sort chosen the API applies
 * the collection's own order, and repeating it here would be a second place to
 * change it.
 */
export const sortParams = (s: SortState): Pick<EngineListParams, 'sortBy' | 'sortOrder'> =>
  (s.field ? { sortBy: s.field, sortOrder: s.order === -1 ? 'desc' : 'asc' } : {});

/** Audit fields every configuration master carries. */
export interface AuditFields {
  appId?: string | null;
  isDelete?: boolean;
  /**
   * Whether the row is in service. There is no editing lifecycle — a row that
   * saved is one that validated — so this is the only state the runtime asks
   * about. Absent on rows written before the field existed, and those were
   * running, so only an explicit `false` means inactive.
   */
  isActive?: boolean | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

/**
 * A named recipient. Exactly one identifier is set, decided by `partyType` —
 * an EXTERNAL party carries a contact rather than an id because it has no
 * account and never will.
 */
export interface Party {
  partyType: string;              // USER | PERSONNEL | EXTERNAL | LIST
  userId?: string | null;
  personnelMasterId?: string | null;
  recipientListId?: string | null;
  contact?: { email?: string; phone?: string; whatsapp?: string } | null;
}

/** Turn a page index and size into the engine's skip/limit. */
export const toSkipLimit = (first: number, rows: number): EngineListParams => ({
  skip: first,
  limit: rows,
});

/** Total records out of a paginated envelope, whichever key the builder used. */
export const totalOf = <T>(resp: PaginatedEnvelope<T>): number =>
  resp.pagination?.totalItems ?? resp.pagination?.total ?? (resp.data || []).length;
